/**
 * Capacitor Haptics wrapper — Phase 1, incrément 20.
 *
 * Vibration tactile pour les actions importantes (toggle status bag
 * item, sign transmission, accept request, etc.). Trois niveaux
 * d'intensité — `light()` est le défaut pour les confirmations
 * subtiles.
 *
 * Stratégie de fallback (du plus capable au moins capable) :
 *   1. Capacitor `Haptics` plugin (iOS / Android natif) → API riche
 *      avec ImpactStyle.
 *   2. `navigator.vibrate(ms)` — disponible en web sur Android
 *      Chrome. Sur iOS Safari, c'est no-op (sans erreur).
 *   3. No-op silencieux.
 *
 * Pas de throw, jamais — un haptic raté ne doit pas casser le flow
 * UI.
 */
import { getPlugin } from './index.js';

interface HapticsPlugin {
  impact(opts: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }): Promise<void>;
  vibrate?(opts?: { duration?: number }): Promise<void>;
  notification?(opts: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }): Promise<void>;
  selectionStart?(): Promise<void>;
  selectionChanged?(): Promise<void>;
  selectionEnd?(): Promise<void>;
}

/** Durées de vibration `navigator.vibrate` par niveau (millisecondes). */
const VIBRATE_DURATION: Record<'light' | 'medium' | 'heavy', number> = {
  light: 10,
  medium: 25,
  heavy: 50,
};

const STYLE_BY_LEVEL: Record<'light' | 'medium' | 'heavy', 'LIGHT' | 'MEDIUM' | 'HEAVY'> = {
  light: 'LIGHT',
  medium: 'MEDIUM',
  heavy: 'HEAVY',
};

async function impact(level: 'light' | 'medium' | 'heavy'): Promise<boolean> {
  // 1. Capacitor natif si dispo
  const plugin = getPlugin<HapticsPlugin>('Haptics');
  if (plugin) {
    try {
      await plugin.impact({ style: STYLE_BY_LEVEL[level] });
      return true;
    } catch {
      // Tomber sur le fallback web
    }
  }
  // 2. Web Vibration API (Android Chrome)
  if (
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { vibrate?: (pattern: number) => boolean }).vibrate ===
      'function'
  ) {
    try {
      (navigator as Navigator & { vibrate: (pattern: number) => boolean }).vibrate(
        VIBRATE_DURATION[level],
      );
      return true;
    } catch {
      // No-op silencieux
    }
  }
  // 3. No-op
  return false;
}

/** Vibration légère (~10ms). Pour les toggles, sélections. */
export const lightImpact = (): Promise<boolean> => impact('light');

/** Vibration moyenne (~25ms). Pour les confirmations de save / submit. */
export const mediumImpact = (): Promise<boolean> => impact('medium');

/** Vibration forte (~50ms). Pour les actions destructives confirmées. */
export const heavyImpact = (): Promise<boolean> => impact('heavy');

/**
 * Notification haptic typée : `success` / `warning` / `error`.
 * Capacitor uniquement — pas de mapping web cohérent. Fallback no-op
 * si plugin absent.
 */
export async function notification(type: 'SUCCESS' | 'WARNING' | 'ERROR'): Promise<boolean> {
  const plugin = getPlugin<HapticsPlugin>('Haptics');
  if (!plugin || typeof plugin.notification !== 'function') return false;
  try {
    await plugin.notification({ type });
    return true;
  } catch {
    return false;
  }
}
